import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Headphones, X, Lock, Play, Pause, MapPin, ChevronRight, ChevronDown, Loader2,
  Navigation, SkipBack, SkipForward, Sparkles, ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const T = {
  banner_title: { it: "Audioguida della città", en: "City audio guide", es: "Audioguía de la ciudad", fr: "Guide audio de la ville" },
  banner_sub: { it: "Scopri i luoghi con la voce. Prima tappa gratis.", en: "Discover places with audio. First stop free.", es: "Descubre lugares con audio. Primera parada gratis.", fr: "Découvrez la ville en audio. Première étape gratuite." },
  listen: { it: "Ascolta", en: "Listen", es: "Escuchar", fr: "Écouter" },
  stops: { it: "tappe", en: "stops", es: "paradas", fr: "étapes" },
  free: { it: "Gratis", en: "Free", es: "Gratis", fr: "Gratuit" },
  locked: { it: "Bloccata", en: "Locked", es: "Bloqueada", fr: "Verrouillée" },
  buy: { it: "Sblocca tutte le tappe", en: "Unlock all stops", es: "Desbloquear todo", fr: "Tout débloquer" },
  buy_cta: { it: "Acquista a", en: "Buy for", es: "Comprar por", fr: "Acheter pour" },
  owned: { it: "Audioguida attiva", en: "Audio guide active", es: "Audioguía activa", fr: "Guide actif" },
  email_ph: { it: "La tua email (ricevuta)", en: "Your email (receipt)", es: "Tu email (recibo)", fr: "Votre email (reçu)" },
  pay: { it: "Paga in sicurezza", en: "Pay securely", es: "Pagar de forma segura", fr: "Payer en sécurité" },
  secure: { it: "Pagamento sicuro con Stripe · 30 giorni di accesso", en: "Secure Stripe payment · 30-day access", es: "Pago seguro con Stripe · 30 días", fr: "Paiement Stripe · accès 30 jours" },
  unlocked: { it: "Audioguida sbloccata! Buon ascolto 🎧", en: "Audio guide unlocked! Enjoy 🎧", es: "¡Audioguía desbloqueada! 🎧", fr: "Audioguide débloqué ! 🎧", de: "Audioguide freigeschaltet! 🎧" },
  unlockRetry: { it: "Pagamento ricevuto, attivazione in corso… se resta bloccata ricarica la pagina tra qualche secondo.", en: "Payment received, activating… if it stays locked, reload the page in a few seconds.", es: "Pago recibido, activando… si sigue bloqueada, recarga en unos segundos.", fr: "Paiement reçu, activation… si cela reste verrouillé, rechargez dans quelques secondes.", de: "Zahlung erhalten, Aktivierung läuft… bei Problemen Seite neu laden." },
  near: { it: "Vicino a te", en: "Near you", es: "Cerca de ti", fr: "Près de vous" },
  locate: { it: "La mia posizione", en: "My location", es: "Mi ubicación", fr: "Ma position", de: "Mein Standort" },
  geo_denied: { it: "Attiva la posizione dalle impostazioni del browser per vedere i punti vicini a te.", en: "Enable location in your browser settings to see nearby stops.", es: "Activa la ubicación en el navegador para ver las paradas cercanas.", fr: "Activez la localisation dans le navigateur pour voir les étapes proches.", de: "Standort im Browser aktivieren, um Stopps in der Nähe zu sehen." },
  geo_unavailable: { it: "Posizione non disponibile al momento. Riprova.", en: "Location unavailable right now. Try again.", es: "Ubicación no disponible. Inténtalo de nuevo.", fr: "Position indisponible. Réessayez.", de: "Standort nicht verfügbar. Erneut versuchen." },
  vat: { it: "IVA inclusa", en: "VAT included", es: "IVA incluido", fr: "TVA incluse" },
  eyebrow: { it: "AUDIOGUIDA", en: "AUDIO GUIDE", es: "AUDIOGUÍA", fr: "GUIDE AUDIO" },
  langs: { it: "lingue", en: "languages", es: "idiomas", fr: "langues" },
  freefirst: { it: "Prima tappa gratis · poi sblocca tutte", en: "First stop free · then unlock all", es: "Primera parada gratis · luego todo", fr: "1re étape gratuite · puis tout débloquer" },
  pay_now: { it: "Paga", en: "Pay", es: "Pagar", fr: "Payer" },
  email_label: { it: "Dove inviamo la ricevuta", en: "Where we send the receipt", es: "Dónde enviamos el recibo", fr: "Où envoyer le reçu" },
};
const tt = (k, l) => (T[k] && (T[k][l] || T[k].it)) || "";

// ── Device ID (localStorage + IndexedDB mirror) ──
function uuid() {
  return (crypto?.randomUUID?.() ||
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }));
}
function idbGet(key) {
  return new Promise((res) => {
    try {
      const req = indexedDB.open("hostland_ag", 1);
      req.onupgradeneeded = () => req.result.createObjectStore("kv");
      req.onsuccess = () => {
        const tx = req.result.transaction("kv", "readonly").objectStore("kv").get(key);
        tx.onsuccess = () => res(tx.result || null);
        tx.onerror = () => res(null);
      };
      req.onerror = () => res(null);
    } catch { res(null); }
  });
}
function idbSet(key, val) {
  try {
    const req = indexedDB.open("hostland_ag", 1);
    req.onupgradeneeded = () => req.result.createObjectStore("kv");
    req.onsuccess = () => { try { req.result.transaction("kv", "readwrite").objectStore("kv").put(val, key); } catch {} };
  } catch {}
}
let _deviceIdPromise = null;
function getDeviceId() {
  // Promise cache a livello di modulo: tutti i mount (anche concorrenti) ricevono
  // lo STESSO device id, altrimenti il device che paga/conferma non combacia con
  // quello che carica lo stato e l'audioguida resta bloccata.
  if (_deviceIdPromise) return _deviceIdPromise;
  _deviceIdPromise = (async () => {
    let id = null;
    try { id = localStorage.getItem("ag_device_id"); } catch {}
    if (!id) id = await idbGet("ag_device_id");
    if (!id) id = uuid();
    try { localStorage.setItem("ag_device_id", id); } catch {}
    idbSet("ag_device_id", id);
    return id;
  })();
  return _deviceIdPromise;
}

const pin = (n, locked, active) => L.divIcon({
  className: "",
  html: `<div style="width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font:800 13px system-ui;background:${active ? "#E5372B" : locked ? "#D4D4D8" : "#18181B"};color:#fff;border:2.5px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.28)">${n}</div>`,
  iconSize: [32, 32], iconAnchor: [16, 16],
});
const meIcon = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 6px rgba(37,99,235,.25)"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

function Fit({ pts }) {
  const map = useMap();
  useEffect(() => {
    if (!pts.length) return;
    try { map.fitBounds(pts.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 15 }); }
    catch { map.setView([pts[0].lat, pts[0].lng], 13); }
  }, [pts, map]);
  return null;
}

const fmtTime = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const fmtDur = (s) => {
  s = Math.floor(s || 0);
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m} min`;
};

export default function AudioguideGuest({ propertyId, lang: wbLang = "it" }) {
  // L'audioguida esiste solo in IT/EN/FR/ES: se il Welcome Book è in tedesco
  // proponiamo l'INGLESE come lingua d'ascolto (nessuna traduzione tedesca).
  const lang = wbLang === "de" ? "en" : wbLang;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [device, setDevice] = useState(null);
  const [current, setCurrent] = useState(null); // tappa_id playing
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [me, setMe] = useState(null);
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [paying, setPaying] = useState(false);
  const audioRef = useRef(null);

  const load = useCallback(async (dev) => {
    try {
      const r = await axios.get(`${API}/audioguide/guest/${propertyId}`, { params: { lang, device: dev } });
      setData(r.data);
    } catch { setData({ available: false }); }
  }, [propertyId, lang]);

  // init device + ritorno da Stripe / recupero — flusso pulito, nessun toast
  useEffect(() => {
    (async () => {
      const dev = await getDeviceId();
      setDevice(dev);
      const qs = new URLSearchParams(window.location.search);
      let sess = qs.get("ag_session");
      const rec = qs.get("ag_recover");
      // Persisti l'intento: anche se il componente viene rimontato (o l'URL è già
      // stato ripulito) non perdiamo il ritorno dal pagamento.
      if (sess) { try { sessionStorage.setItem("ag_pending_session", sess); } catch {} }
      else { try { sess = sessionStorage.getItem("ag_pending_session"); } catch {} }
      if (sess || rec) {
        // Apri subito il player e pulisci l'URL: nessuna pagina strana, nessun flash.
        setOpen(true);
        qs.delete("ag_session"); qs.delete("ag_cancel"); qs.delete("ag_recover");
        window.history.replaceState({}, "", window.location.pathname + (qs.toString() ? "?" + qs : ""));
      }
      await load(dev);
      if (sess) {
        // Conferma il pagamento e sblocca tutte le tappe; riprova in silenzio.
        for (let attempt = 0; attempt < 6; attempt++) {
          try {
            const r = await axios.post(`${API}/audioguide/purchase/confirm`, { session_id: sess, device: dev });
            if (r.data.ok) { await load(dev); break; }
          } catch (e) { /* ritenta: il pagamento può propagarsi con un piccolo ritardo */ }
          await new Promise((res) => setTimeout(res, 1500));
          await load(dev); // il webhook potrebbe aver già sbloccato lato server
        }
        try { sessionStorage.removeItem("ag_pending_session"); } catch {}
      }
      if (rec) {
        try { await axios.post(`${API}/audioguide/recover`, { recovery_token: rec, device: dev }); await load(dev); } catch {}
      }
    })();
  }, [load]);

  // geolocation when modal opens
  useEffect(() => {
    if (!open || !navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setMe({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [open]);

  // manual "locate me" — strong user gesture → reliable permission prompt
  const locateMe = useCallback(() => {
    if (!navigator.geolocation) { toast.error(tt("geo_unavailable", lang)); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const loc = { lat: p.coords.latitude, lng: p.coords.longitude };
        setMe(loc);
        setLocating(false);
        try { mapRef.current?.flyTo([loc.lat, loc.lng], 16, { duration: 0.8 }); } catch {}
      },
      (err) => {
        setLocating(false);
        toast.error(tt(err?.code === 1 ? "geo_denied" : "geo_unavailable", lang));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [lang]);

  const setMedia = (t) => {
    if (!("mediaSession" in navigator)) return;
    try {
      const art = data?.city?.cover || data?.city?.image;
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: t.title || "Audioguida",
        artist: (data?.city?.name) || "Audioguida",
        album: "Hostland · Audioguida della città",
        artwork: art ? [{ src: art, sizes: "512x512", type: "image/jpeg" }] : [],
      });
      navigator.mediaSession.playbackState = "playing";
      navigator.mediaSession.setActionHandler("play", () => { audioRef.current?.play(); setPlaying(true); try { navigator.mediaSession.playbackState = "playing"; } catch (_) {} });
      navigator.mediaSession.setActionHandler("pause", () => { audioRef.current?.pause(); setPlaying(false); try { navigator.mediaSession.playbackState = "paused"; } catch (_) {} });
      navigator.mediaSession.setActionHandler("previoustrack", () => step(-1));
      navigator.mediaSession.setActionHandler("nexttrack", () => step(1));
      try { navigator.mediaSession.setActionHandler("seekto", (e) => { if (audioRef.current && e.seekTime != null) { audioRef.current.currentTime = e.seekTime; } }); } catch (_) {}
    } catch (_) {}
  };

  const play = async (t) => {
    if (t.locked) { setBuyOpen(true); return; }
    const a = audioRef.current;
    // Se è già la traccia corrente → toggle immediato
    if (t.tappa_id === current && a.src) {
      if (a.paused) { try { await a.play(); setPlaying(true); } catch (_) {} }
      else { a.pause(); setPlaying(false); }
      return;
    }
    let token = t.audio_token;
    if (!token) {
      try {
        const r = await axios.post(`${API}/audioguide/guest/${propertyId}/token`, { tappa_id: t.tappa_id, lang, device });
        token = r.data.audio_token;
      } catch { return; }
    }
    // Cambio sorgente pulito: ferma la precedente, aggiorna lo stato subito, carica e parte
    try { a.pause(); } catch (_) {}
    setCurrent(t.tappa_id);
    setPos(0); setDur(0);
    a.src = `${API}/audioguide/audio?t=${encodeURIComponent(token)}`;
    a.load();
    try { await a.play(); setPlaying(true); setMedia(t); }
    catch (_) { setPlaying(false); }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a.src) { const first = data?.tappe?.find((x) => !x.locked); if (first) play(first); return; }
    if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => {}); } else { a.pause(); setPlaying(false); }
  };

  const step = (dir) => {
    const list = data?.tappe || [];
    const i = list.findIndex((x) => x.tappa_id === current);
    for (let j = i + dir; j >= 0 && j < list.length; j += dir) {
      if (!list[j].locked) { play(list[j]); return; }
    }
  };

  const startCheckout = async () => {
    if (!email.includes("@")) return;
    setPaying(true);
    try {
      const r = await axios.post(`${API}/audioguide/purchase/checkout`, {
        property_id: propertyId, device, email, lang,
        origin_url: window.location.origin + window.location.pathname,
      });
      window.location.href = r.data.url;
    } catch { setPaying(false); }
  };

  if (!data || !data.available) return null;
  const { city, tappe, access } = data;
  const curT = tappe.find((x) => x.tappa_id === current);
  const pts = tappe.filter((t) => t.lat && t.lng);

  return (
    <>
      <audio
        ref={audioRef}
        playsInline
        onPlay={() => { setPlaying(true); if ("mediaSession" in navigator) { try { navigator.mediaSession.playbackState = "playing"; } catch (_) {} } }}
        onPause={() => { setPlaying(false); if ("mediaSession" in navigator) { try { navigator.mediaSession.playbackState = "paused"; } catch (_) {} } }}
        onTimeUpdate={(e) => {
          setPos(e.target.currentTime); setDur(e.target.duration || 0);
          if ("mediaSession" in navigator && navigator.mediaSession.setPositionState && e.target.duration) {
            try { navigator.mediaSession.setPositionState({ duration: e.target.duration, position: e.target.currentTime, playbackRate: e.target.playbackRate || 1 }); } catch (_) {}
          }
        }}
        onEnded={() => { setPlaying(false); step(1); }}
        preload="none"
      />

      {/* ── Banner (Soggiorno) — stile card nera ── */}
      <div className="rise" data-testid="audioguide-banner">
        <button
          onClick={() => setOpen(true)}
          style={{ position: "relative", overflow: "hidden", width: "100%", textAlign: "left", background: "#1A0B2E", color: "#fff", borderRadius: 24, padding: 18, minHeight: 196, display: "flex", flexDirection: "column", justifyContent: "space-between", border: "none", cursor: "pointer" }}>
          <span className="wave-on" style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 40 }}>
            {[14, 26, 18, 34, 22, 38, 16, 30, 24, 36, 20, 32, 15, 28].map((h, i) => (
              <span key={i} style={{ width: 5, height: h, borderRadius: 3, background: "#FFC700", transformOrigin: "bottom", animationDelay: `${i * 0.08}s` }} />
            ))}
          </span>
          <span data-testid="audioguide-langs" style={{ position: "absolute", top: 16, right: 16, display: "flex", gap: 6, fontSize: 19, lineHeight: 1 }}>
            <span role="img" aria-label="Italiano">🇮🇹</span><span role="img" aria-label="Español">🇪🇸</span><span role="img" aria-label="Français">🇫🇷</span><span role="img" aria-label="English">🇬🇧</span>
          </span>
          {access?.active && (
            <span data-testid="audioguide-owned-badge" style={{ position: "absolute", top: 48, right: 16, fontSize: 10, fontWeight: 800, background: "rgba(255,255,255,.18)", borderRadius: 999, padding: "3px 9px" }}>{tt("owned", lang)}</span>
          )}
          <span style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
            <span style={{ minWidth: 0 }}>
              <span className="disp" style={{ display: "block", fontSize: 30, fontWeight: 800, lineHeight: .98 }}>
                {({ it: `${city.name} raccontata a voce`, en: `${city.name} in audio`, es: `${city.name} en audio`, fr: `${city.name} en audio` })[lang] || `${city.name} raccontata a voce`}
              </span>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 500, color: "rgba(255,255,255,.82)", marginTop: 8, lineHeight: 1.35 }}>
                {({
                  it: `${city.tappe_count} tappe sulla mappa. Ascolta mentre cammini, in 4 lingue.`,
                  en: `${city.tappe_count} stops on the map. Listen as you walk, in 4 languages.`,
                  es: `${city.tappe_count} paradas en el mapa. Escucha mientras caminas, en 4 idiomas.`,
                  fr: `${city.tappe_count} étapes sur la carte. Écoutez en marchant, en 4 langues.`,
                })[lang] || `${city.tappe_count} tappe sulla mappa, in 4 lingue.`}
              </span>
              <span className="disp" style={{ display: "block", fontSize: 24, fontWeight: 800, marginTop: 10 }}>
                {city.price_eur.toFixed(2).replace(".", ",")} € <span style={{ fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,.7)" }}>{({ it: "/ 30 giorni", en: "/ 30 days", es: "/ 30 días", fr: "/ 30 jours" })[lang] || "/ 30 giorni"}</span>
              </span>
            </span>
            <span style={{ width: 58, height: 58, borderRadius: 29, background: "#FFC700", color: "#1A0B2E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Play size={24} fill="#1A0B2E" /></span>
          </span>
        </button>
      </div>

      {/* ── Player a schermo pieno (light · bianco/rosso) ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[9999] flex flex-col"
            style={{ background: "#FFFFFF" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="audioguide-modal">

            {/* header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0 border-b" style={{ borderColor: "#F1F1F3" }}>
              <button onClick={() => { audioRef.current?.pause(); setPlaying(false); setOpen(false); }} className="w-11 h-11 rounded-full flex items-center justify-center active:scale-95 transition-transform" style={{ background: "#F4F4F5" }} data-testid="audioguide-close"><ChevronDown className="w-6 h-6" style={{ color: "#18181B" }} /></button>
              <div className="text-center min-w-0 px-2">
                <p className="text-[11px] font-black tracking-[.24em]" style={{ color: "#E5372B" }}>{tt("eyebrow", lang)}</p>
                <p className="font-black leading-tight truncate" style={{ color: "#18181B", fontSize: 16 }}>{city.name}</p>
              </div>
              <div className="w-11 h-11 shrink-0" />
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-48">
              {/* mappa hero */}
              <div className="relative rounded-[28px] overflow-hidden mt-4" style={{ height: "30vh", boxShadow: "0 20px 44px -22px rgba(0,0,0,.4)" }}>
                <MapContainer ref={mapRef} center={[city.center.lat || pts[0]?.lat || 44.4, city.center.lng || pts[0]?.lng || 8.9]} zoom={13} style={{ height: "100%", width: "100%" }} zoomControl={false} attributionControl={false}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Fit pts={me ? [...pts, me] : pts} />
                  {pts.map((t) => (
                    <Marker key={t.tappa_id} position={[t.lat, t.lng]} icon={pin(t.order + 1, t.locked, t.tappa_id === current)}
                      eventHandlers={{ click: () => play(t) }} />
                  ))}
                  {me && <Marker position={[me.lat, me.lng]} icon={meIcon} />}
                </MapContainer>
                <button
                  type="button"
                  onClick={locateMe}
                  disabled={locating}
                  data-testid="audioguide-locate-btn"
                  aria-label={tt("locate", lang)}
                  className="absolute top-3 right-3 w-11 h-11 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform disabled:opacity-70"
                  style={{ background: "#fff", color: me ? "#2563EB" : "#18181B", zIndex: 1000 }}
                >
                  {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Navigation className="w-5 h-5" fill={me ? "#2563EB" : "none"} />}
                </button>
                {me && (
                  <div className="absolute bottom-3 right-3 rounded-full px-3 py-1.5 text-[11px] font-black flex items-center gap-1.5 shadow-lg" style={{ background: "#E5372B", color: "#fff", zIndex: 1000 }}>
                    <Navigation className="w-3.5 h-3.5" /> {tt("near", lang)}
                  </div>
                )}
              </div>

              {/* titolo + play grande */}
              <div className="flex items-end justify-between gap-4 mt-6">
                <div className="min-w-0">
                  <h2 className="disp" style={{ fontSize: 36, fontWeight: 800, lineHeight: .96, color: "#18181B", letterSpacing: "-.02em" }}>
                    {({ it: `${city.name} raccontata a voce`, en: `${city.name} in audio`, es: `${city.name} en audio`, fr: `${city.name} en audio` })[lang] || `${city.name} raccontata a voce`}
                  </h2>
                  <p className="mt-3 font-bold" style={{ fontSize: 14, color: "#71717A" }}>{city.tappe_count} {tt("stops", lang)} · {fmtDur(city.total_duration_sec)} · 4 {tt("langs", lang)}{access?.active ? ` · ${tt("owned", lang)}` : ""}</p>
                </div>
                <button onClick={toggle} className="shrink-0 flex items-center justify-center active:scale-95 transition-transform" style={{ width: 66, height: 66, borderRadius: 33, background: "#E5372B", boxShadow: "0 16px 32px -10px rgba(229,55,43,.65)" }} data-testid="audioguide-hero-play">
                  {playing ? <Pause className="w-8 h-8" style={{ color: "#fff" }} fill="#fff" /> : <Play className="w-8 h-8 ml-1" style={{ color: "#fff" }} fill="#fff" />}
                </button>
              </div>

              {/* CTA sblocco */}
              {!access?.active && (
                <button onClick={() => setBuyOpen(true)} className="w-full mt-6 rounded-[22px] p-4 flex items-center gap-3.5 text-left active:scale-[.99] transition-transform" style={{ background: "linear-gradient(135deg,#E5372B,#C62B21)", boxShadow: "0 18px 36px -16px rgba(229,55,43,.7)" }} data-testid="audioguide-unlock-btn">
                  <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,.22)" }}><Sparkles className="w-6 h-6 text-white" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-black leading-tight" style={{ fontSize: 16 }}>{tt("buy", lang)}</p>
                    <p className="text-white/90 mt-0.5" style={{ fontSize: 12.5 }}>{tt("freefirst", lang)}</p>
                  </div>
                  <span className="shrink-0 font-black text-white" style={{ fontSize: 22 }}>€{city.price_eur.toFixed(2).replace(".", ",")}</span>
                </button>
              )}

              {/* lista tappe */}
              <div className="mt-6 space-y-1.5">
                {tappe.map((t) => {
                  const isCur = t.tappa_id === current;
                  return (
                    <button key={t.tappa_id} onClick={() => play(t)}
                      className="w-full rounded-2xl p-3 flex items-center gap-3.5 text-left transition-colors"
                      style={{ background: isCur ? "rgba(229,55,43,.08)" : "transparent", border: isCur ? "1.5px solid rgba(229,55,43,.28)" : "1.5px solid #F1F1F3" }}
                      data-testid={`audioguide-stop-${t.order}`}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-black" style={{ fontSize: 15, background: t.locked ? "#F4F4F5" : isCur ? "#E5372B" : "#18181B", color: t.locked ? "#A1A1AA" : "#fff" }}>
                        {t.locked ? <Lock className="w-5 h-5" /> : isCur && playing ? (
                          <span className="wave-on" style={{ display: "flex", alignItems: "flex-end", gap: 2.5, height: 18 }}>
                            {[11, 18, 9, 15].map((h, i) => <span key={i} style={{ width: 3, height: h, borderRadius: 2, background: "#fff", transformOrigin: "bottom", animationDelay: `${i * 0.1}s` }} />)}
                          </span>
                        ) : t.order + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold leading-tight truncate" style={{ fontSize: 15.5, color: t.locked ? "#A1A1AA" : isCur ? "#E5372B" : "#18181B" }}>{t.title}</p>
                        <p className="mt-1 flex items-center gap-2" style={{ fontSize: 12, color: "#A1A1AA" }}>
                          {t.free && <span style={{ color: "#16A34A", fontWeight: 800 }}>{tt("free", lang)}</span>}
                          {t.locked && <span style={{ fontWeight: 700 }}>{tt("locked", lang)}</span>}
                          {t.duration_sec > 0 && <span>{fmtTime(t.duration_sec)}</span>}
                        </p>
                      </div>
                      {t.locked ? <Lock className="w-5 h-5 shrink-0" style={{ color: "#D4D4D8" }} /> :
                        (isCur && playing ? <Pause className="w-6 h-6 shrink-0" style={{ color: "#E5372B" }} /> : <Play className="w-6 h-6 shrink-0" style={{ color: isCur ? "#E5372B" : "#18181B" }} />)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* mini-player sticky */}
            {curT && (
              <div className="absolute left-4 right-4 bottom-5 rounded-[24px] px-4 py-3.5" style={{ background: "#FFFFFF", border: "1.5px solid #EDEDF0", boxShadow: "0 22px 48px -16px rgba(0,0,0,.28)" }} data-testid="audioguide-player">
                <div className="flex items-center gap-3.5">
                  <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#E5372B" }}><Headphones className="w-6 h-6" style={{ color: "#fff" }} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-black truncate" style={{ fontSize: 15, color: "#18181B" }}>{curT.title}</p>
                    <p className="truncate" style={{ fontSize: 12, color: "#A1A1AA" }}>{city.name}</p>
                  </div>
                  <button onClick={() => step(-1)} className="active:scale-90 transition-transform" data-testid="audioguide-prev"><SkipBack className="w-6 h-6" style={{ color: "#52525B" }} fill="#52525B" /></button>
                  <button onClick={toggle} className="w-13 h-13 rounded-full flex items-center justify-center shrink-0 active:scale-95 transition-transform" style={{ width: 52, height: 52, background: "#E5372B", boxShadow: "0 10px 22px -8px rgba(229,55,43,.7)" }} data-testid="audioguide-playpause">
                    {playing ? <Pause className="w-6 h-6" style={{ color: "#fff" }} fill="#fff" /> : <Play className="w-6 h-6 ml-0.5" style={{ color: "#fff" }} fill="#fff" />}
                  </button>
                  <button onClick={() => step(1)} className="active:scale-90 transition-transform" data-testid="audioguide-next"><SkipForward className="w-6 h-6" style={{ color: "#52525B" }} fill="#52525B" /></button>
                </div>
                <div className="flex items-center gap-2.5 mt-3">
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#A1A1AA", width: 34, textAlign: "right" }}>{fmtTime(pos)}</span>
                  <input type="range" min={0} max={dur || 0} value={pos} onChange={(e) => { audioRef.current.currentTime = +e.target.value; setPos(+e.target.value); }}
                    className="flex-1 h-1.5" style={{ accentColor: "#E5372B" }} data-testid="audioguide-seek" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#A1A1AA", width: 34 }}>{fmtTime(dur)}</span>
                </div>
              </div>
            )}

            {/* bottom-sheet acquisto */}
            <AnimatePresence>
              {buyOpen && (
                <motion.div className="absolute inset-0 flex items-end justify-center" style={{ background: "rgba(24,24,27,.45)", backdropFilter: "blur(4px)" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !paying && setBuyOpen(false)}>
                  <motion.div className="w-full max-w-md rounded-t-[32px] p-6 pb-8" style={{ background: "#FFFFFF", boxShadow: "0 -20px 50px -20px rgba(0,0,0,.3)" }} initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }} transition={{ type: "spring", damping: 32, stiffness: 320 }} onClick={(e) => e.stopPropagation()} data-testid="audioguide-buy-sheet">
                    <div className="w-11 h-1.5 rounded-full mx-auto mb-6" style={{ background: "#E4E4E7" }} />
                    <div className="flex items-center gap-3.5 mb-1">
                      <span className="w-13 h-13 rounded-2xl flex items-center justify-center shrink-0" style={{ width: 52, height: 52, background: "#E5372B" }}><Headphones className="w-6 h-6" style={{ color: "#fff" }} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="font-black leading-tight" style={{ fontSize: 19, color: "#18181B" }}>{tt("buy", lang)}</p>
                        <p className="truncate" style={{ fontSize: 12.5, color: "#A1A1AA" }}>{city.name} · {city.tappe_count} {tt("stops", lang)} · {tt("vat", lang)}</p>
                      </div>
                      <p className="font-black shrink-0" style={{ fontSize: 26, color: "#E5372B" }}>€{city.price_eur.toFixed(2).replace(".", ",")}</p>
                    </div>
                    <p className="mb-5 mt-2.5 flex items-center gap-1.5" style={{ fontSize: 12.5, color: "#16A34A", fontWeight: 700 }}><Sparkles className="w-4 h-4" /> {tt("freefirst", lang)}</p>

                    <label className="block font-bold mb-2" style={{ fontSize: 12.5, color: "#71717A" }}>{tt("email_label", lang)}</label>
                    <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.com"
                      className="w-full rounded-xl px-4 py-3.5 mb-5 focus:outline-none" style={{ fontSize: 15, background: "#F4F4F5", border: "1.5px solid #E4E4E7", color: "#18181B" }} data-testid="audioguide-email" />

                    <button onClick={startCheckout} disabled={paying || !email.includes("@")}
                      className="w-full font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[.99] disabled:opacity-45"
                      style={{ fontSize: 16, background: "#E5372B", color: "#fff", boxShadow: "0 16px 32px -10px rgba(229,55,43,.6)" }} data-testid="audioguide-pay-btn">
                      {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-5 h-5" /> {tt("pay_now", lang)} €{city.price_eur.toFixed(2).replace(".", ",")}</>}
                    </button>
                    <p className="text-center mt-4 flex items-center justify-center gap-1.5" style={{ fontSize: 11.5, color: "#A1A1AA" }}><ShieldCheck className="w-4 h-4" /> {tt("secure", lang)}</p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
